<template>
  <div id="donate-step2" class="donation-supporter-details">
    <h2>Your details</h2>

    <!-- On Behalf of Business -->
    <div>
      <label class="checkbox">
        {{ product.producttype == "adoption" ? "Adopt" : "Donate" }} on behalf
        of a business
        <input
          type="checkbox"
          id="isBehalfBusiness"
          name="isBehalfBusiness"
          v-model="donation.isBehalfBusiness"
        />
        <span class="checkmark"></span>
      </label>
    </div>

    <!-- Business Name -->
    <div class="wwf-field" v-if="donation.isBehalfBusiness">
      <input
        name="businessName"
        type="text"
        v-model="donation.bussiness.name"
        v-validate="'required'"
        :class="{
          error: errors.has('businessName'),
          empty: !donation.bussiness.name,
        }"
        autocomplete="organization"
      />
      <label for="businessName">Business Name</label>
    </div>

    <!-- Title -->
    <div class="wwf-field">
      <select
        name="title"
        v-model="donation.title"
        v-validate="'required'"
        :class="{ error: errors.has('title'), empty: !donation.title }"
        autocomplete="honorific-prefix"
      >
        <option value="Dr">Dr</option>
        <option value="Miss">Miss</option>
        <option value="Mr">Mr</option>
        <option value="Mrs">Mrs</option>
        <option value="Ms">Ms</option>
        <option value="Mx">Mx</option>
      </select>
      <label for="title">Select a title</label>
    </div>

    <!-- First Name -->
    <div class="wwf-field">
      <input
        name="firstName"
        type="text"
        v-model="donation.firstname"
        v-validate="'required'"
        :class="{ error: errors.has('firstName'), empty: !donation.firstname }"
        autocomplete="given-name"
      />
      <label for="firstName">First Name</label>
    </div>

    <!-- Last Name -->
    <div class="wwf-field">
      <input
        name="lastName"
        type="text"
        v-model="donation.lastname"
        v-validate="'required'"
        :class="{ error: errors.has('lastName'), empty: !donation.lastname }"
        autocomplete="family-name"
      />
      <label for="lastName">Last Name</label>
    </div>

    <!-- Phone Number -->
    <div class="wwf-field">
      <input
        name="phone"
        type="tel"
        v-model="donation.phone"
        min="8"
        max="12"
        v-validate="{ required: true, donationValidatePhone: true }"
        :class="{
          error: errors.has('phone'),
          empty: !donation.phone,
        }"
        autocomplete="tel"
      />
      <label for="phone">Phone</label>

      <span class="wwf-alert">
        <p v-if="errors.has('phone')">
          {{ errors.first("phone") }}
        </p>
      </span>
    </div>

    <!-- Email -->
    <div class="wwf-field">
      <input
        id="email"
        name="email"
        type="email"
        v-model="donation.email"
        v-validate="{ required: true, donationValidateEmail: true }"
        :class="{
          error: errors.has('email') || errors.has('donationValidateEmail'),
          empty: !donation.email,
        }"
        autocomplete="email"
      />
      <label for="email">Email</label>

      <span class="wwf-alert">
        <p v-if="errors.firstByRule('email', 'donationValidateEmail')">
          {{ errors.first("email") }}
        </p>
      </span>
    </div>

    <!-- Marketing Emails -->
    <label class="checkbox">
      Stay up to date by receiving emails
      <input type="checkbox" checked v-model="donation.optin" />
      <span class="checkmark"></span>
    </label>

    <div v-if="showBequest">
      <!-- Bequest | Give in will -->
      <label class="checkbox" v-if="!showCompliance">
        Send me information about including WWF in my Will
        <input type="checkbox" checked v-model="donation.bequest" />
        <span class="checkmark"></span>
      </label>

      <!-- Partnership Compliance -->
      <label class="checkbox" v-if="showCompliance">
        <span class="statement">
          I declare that my core business is not engaged in the outlined
          <a :href="blacklistUrl" target="_blank">black listed</a> industries.
        </span>
        <input
          type="checkbox"
          name="compliance"
          v-validate="'required'"
          v-model="donation.compliance"
        />
        <span
          class="checkmark"
          :class="{ complianceError: errors.has('compliance') }"
        ></span>
      </label>
    </div>

    <h2>Your address</h2>

    <label class="checkbox" v-if="showRecipientCheckbox">
      Same as Recipient
      <input type="checkbox" checked v-model="donation.isSameAddress" />
      <span class="checkmark"></span>
    </label>

    <div class="cannot-deliver" v-if="cannotDeliver">
      We're sorry, gifts and toy products can only be delivered within
      Australia.
    </div>

    <AddressInput
      ref="AddressInput"
      v-show="!showRecipientCheckbox || !donation.isSameAddress"
    ></AddressInput>

    <!-- Next button -->
    <button
      name="goto-payment"
      type="button"
      v-bind:class="{ plain: !isSaving }"
      v-on:click="next()"
      :disabled="isSaving"
      v-bind:style="[isSaving ? { 'background-color': 'var(--wwfBlue)' } : {}]"
      href="#form-top"
    >
      <span v-if="donation.payementType === 'wwf_paypal' && !isSaving"
        >Pay with PayPal</span
      >
      <span v-if="donation.payementType !== 'wwf_paypal' && !isSaving"
        >Go to Payment</span
      >
      <i v-if="!isSaving" class="fas fa-arrow-right"></i>
      <img
        v-else
        class="center loading resize"
        alt="Loading while checking details"
        src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/3915/dist/assets/img/loading-three-dots.svg"
      />
    </button>
  </div>
</template>

<script>
import AddressInput from "./AddressInput.vue";

import { mapState } from "vuex";
import { Validator } from "vee-validate";

import { getNameByDonationType } from "../../helpers";
import gtm from "../../utils/gtm";

export default {
  name: "SupporterDetails",
  components: {
    AddressInput,
  },

  data() {
    return {
      isSaving: false,
      emailCheck: {
        timeout: null,
        lastTimestamp: Date.now(),
        emailRegex: /.+\@.+\..+/,
        checkEmailLoading: false,
      },
      phoneNumberCheck: {
        timeout: null,
        lastTimestamp: Date.now(),
        checkEmailLoading: false,
      },
    };
  },
  computed: {
    showCompliance() {
      return this.product.extrasetting.showCompliance;
    },
    showRecipientCheckbox() {
      return this.product.producttype == "adoption" && this.donation.isGift;
    },
    cannotDeliver() {
      return (
        this.country != "Australia" &&
        this.product.producttype == "adoption" &&
        !this.donation.isGift // Display the message if there's no recipient and only the supporter
      );
    },
    isAustralia() {
      if (this.donation.isGift && this.donation.isSameAddress) {
        return this.donation.gift.country === "Australia";
      }
      return this.country === "Australia";
    },
    ...mapState({
      donation: (state) => state.donation.donation,
      product: (state) => state.product.product,
      country: (state) => state.donation.donation.addressInput.country,
    }),
  },
  props: {
    blacklistUrl: {
      type: String,
      default:
        "https://www.wwf.org.au/get-involved/partnerships/who-we-partner-with#blacklist",
    },
    showBequest: {
      type:Boolean,
      default: true,
    }
  },

  created() {
    // EMAIL VALIDATION

    const donationValidateEmail = (email) => {
      if (email.length > 0 && this.emailCheck.emailRegex.test(email)) {
        return new Promise((resolve, reject) => {
          let timestamp = Date.now(); // timestamp for this api call
          this.emailCheck.lastTimestamp = timestamp; // 1 , 2 , 3
          this.emailCheck.checkEmailLoading = true;
          // Fetch Suggestions
          this.$store
            .dispatch("validateEmail", email)
            .then((response) => {
              if (response && timestamp >= this.emailCheck.lastTimestamp) {
                this.emailCheck.checkEmailLoading = false;
                this.donation.emailValidation = response;
                resolve(response);
              }
            })
            .catch((err) => {
              console.error(err);
              this.emailCheck.checkEmailLoading = false;
              // If the call failed, do not keep the user from donating
              resolve({
                valid: true,
              });
            });
        });
      } else {
        return {
          valid: false,
          data: { message: "Email invalid" },
        };
      }
    };

    Validator.extend("donationValidateEmail", {
      validate: donationValidateEmail,
      getMessage: (field, params, data) => data.message,
    });

    // PHONE NUMBER VALIDATION
    const donationValidatePhone = (phone) => {
      // Only check for australian numbers
      if (!this.isAustralia) {
        const regex = /^\+?[0-9\s]*$/;
        if (phone.match(regex)) {
          return { valid: true };
        }
        return {
          valid: false,
          data: {
            message: "Please only use a plus sign, spaces and numeric values",
          },
        };
      } else if (phone.length > 0) {
        return new Promise((resolve, reject) => {
          let timestamp = Date.now(); // timestamp for this api call
          this.phoneNumberCheck.lastTimestamp = timestamp; // 1 , 2 , 3
          this.phoneNumberCheck.checkPhoneLoading = true;
          // Fetch Suggestions
          this.$store
            .dispatch("validatePhone", phone)
            .then((response) => {
              if (
                response &&
                timestamp >= this.phoneNumberCheck.lastTimestamp
              ) {
                this.phoneNumberCheck.checkPhoneLoading = false;
                resolve(response);
              }
            })
            .catch((err) => {
              console.error(err);
              this.phoneNumberCheck.checkPhoneLoading = false;
              // If the call failed, do not keep the user from donating
              resolve({
                valid: true,
              });
            });
        });
      } else {
        return {
          valid: false,
          data: { message: "Invalid phone number, try again" },
        };
      }
    };

    Validator.extend("donationValidatePhone", {
      validate: donationValidatePhone,
      getMessage: (field, params, data) => data.message,
    });

    // Turn on 'Donate on behalf of business' by default if displaying compliance checkbox
    if (this.showCompliance) {
      this.donation.isBehalfBusiness = true;
    }
  },
  methods: {
    async next() {
      // Check if it is already saving
      if (this.isSaving) {
        return;
      }
      this.isSaving = true;

      try {
        if (this.showRecipientCheckbox && this.donation.isSameAddress) {
          // Validate form
          await this.validate();

          // Transfer
          this.transferRecipientAddress();
        } else {
          // Validate form
          await Promise.all([
            this.validate(),
            this.$refs.AddressInput.validate(),
          ]);

          // Transfer
          this.donation.billing = {
            ...this.donation.billing,
            ...this.$refs.AddressInput.getAddressDetails(),
          };
        }

        // Commit the current step (Push to database)
        await this.commit();

        // Fire GTM Event
        gtm.pushCheckout(
          this.product.productname,
          this.product.id,
          this.donation.amount,
          this.product.producttype,
          getNameByDonationType(this.donation.type)
        );

        this.$store.dispatch("logFormSupporter");

        // Go to checkout
        // For Paypal, redirect to payment page
        // For other, go to next step
        if (this.donation.payementType == "wwf_paypal") {
          this.$store.dispatch("logFormPayAttempt");
          this.$store
            .dispatch("checkoutPaypal")
            .then((response) => {
              this.isSaving = false;
              window.location.href = response;
            })
            .catch((error) => {
              this.isSaving = false;
              console.error("Checkout Paypal - Error - ", error);
              this.$store.dispatch("logFormPayFail", "Paypal checkout error");
              reject(error);
            });
        } else {
          this.$emit("next");
          this.isSaving = false;
        }
      } catch (err) {
        this.isSaving = false;
        console.error("Next - Error - ", err);
      }
    },
    validate() {
      return new Promise((resolve, reject) => {
        this.$validator
          .validateAll()
          .then((result) => {
            // Will return true if valid and false if not
            if (result) {
              resolve(result);
            } else {
              console.error("Supporter Details not valid - result", result);
              reject(result);
            }
          })
          .catch((err) => {
            console.error("Supporter Details Validation - Error - ", err);
            reject(err);
          });
      });
    },
    commit() {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch("saveDonation")
          .then((response) => {
            resolve();
          })
          .catch((error) => {
            console.error("saveDonation1 error", error);
            reject(error);
          });
      });
    },
    transferRecipientAddress() {
      const { title, firstname, lastname, ...address } = this.donation.gift;

      this.donation.billing = address;
      this.donation.gift = {
        title,
        firstname,
        lastname,
        unitnumber: null,
        streetno: null,
        streetname: null,
        postcode: null,
        state: null,
        suburb: null,
        country: null,
      };
    },
  },
};
</script>

<style scoped>
.cannot-deliver {
  margin-bottom: 1rem;
  color: var(--errorColor);
}

.statement {
  font-weight: bold;
}

#donate-step2 .checkmark.complianceError {
  border-color: var(--wwfRed);
}

.loading {
  filter: brightness(0) invert(1);
}

.center {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.resize {
  width: 10%;
  height: 10%;
}
</style>


// WEBPACK FOOTER //
// SupporterDetails.vue?795c27c8